import type { RoutineMeta } from '@/core/engines/intellisenseTypes';
import type { RoutineMetadata } from '@/types/electron';

/**
 * Project the renderer-side `Record<string, RoutineMetadata[]>` shape that
 * PG / MySQL / Oracle caches all share into the bus's `RoutineMeta[]` shape.
 *
 * `routineType` heuristic: if the cache says `'PROCEDURE'` (case-insensitive)
 * we map to `'procedure'`; everything else (including missing) falls back to
 * `'function'`. The native caches don't distinguish aggregate / window kinds
 * — those are out of scope until per-engine resolver slices need them.
 *
 * Parameter `mode` is derived from `isOutput`: `true` → `'out'`, otherwise
 * `'in'`. `'inout'` and `'return'` are not surfaced today; the native
 * `RoutineParameter` shape only carries the boolean.
 */
export function projectRoutines(
  routinesByNameLower: Readonly<Record<string, readonly RoutineMetadata[]>>,
  database: string | undefined,
): RoutineMeta[] {
  const out: RoutineMeta[] = [];
  for (const list of Object.values(routinesByNameLower)) {
    for (const r of list) {
      out.push({
        name: r.name,
        schema: r.schema || undefined,
        database,
        kind: r.routineType?.toUpperCase() === 'PROCEDURE' ? 'procedure' : 'function',
        returns: r.returns,
        parameters: r.parameters.map((p) => ({
          name: p.name,
          dataType: p.dataType,
          mode: p.isOutput ? 'out' : 'in',
        })),
      });
    }
  }
  return out;
}
